<script lang="ts" setup>
import { ref, onMounted, computed, watch, inject } from 'vue'
import { useSessionStore } from '@/store/sessionStore'
import axiosInstance from '@/plugins/axios'
import { useToast } from 'primevue/usetoast'
import { useI18n } from 'vue-i18n'
import { SettingsPageTemplate } from '@/components'
import { ITenant } from '@airin/shared-types'

const sessionStore = useSessionStore()
const toast = useToast()
const allTranslations = ref([])
const tenantDetails = ref<ITenant>({} as ITenant)
const gF = inject('gF') as any
const { t } = useI18n()
const customTranslations = ref<any[]>([])
const defaultLanguages = ref(['670e1e32d4634dd3c3ec7478', '670fad078be48456eb53b250'])

const fallbackLanguageOptions = computed(() => {
  const enabledLangs = tenantDetails.value.adminSettings?.enabledLanguages || []
  return enabledLangs.filter((lang: string) => defaultLanguages.value.includes(lang))
})

const faqFallbackBinding = computed({
  get: () => tenantDetails.value.settings?.faqFallbackLanguage ?? 'none',
  set: (val: string) => {
    tenantDetails.value.settings.faqFallbackLanguage = val === 'none' ? null : val
  }
})

const getAllTranslations = async () => {
  const res = await axiosInstance.get('/admin/getAllTranslationsMinified')
  allTranslations.value = res.data
}

const getCustomTranslations = async () => {
  try {
    for (const lang of tenant.value.adminSettings.enabledLanguages) {
      const response = await axiosInstance.get(`/translations/${lang}`)
      if (
        response.data.languageIdentifier === 'ailab-general-de' ||
        response.data.languageIdentifier === 'ailab-general-en'
      ) {
        continue
      }
      customTranslations.value.push(response.data)
    }
  } catch (error) {
    console.log(error)
  }
}

const tenant = computed(
  () =>
    sessionStore.getTenant() || {
      id: '',
      name: '',
      domains: [],
      adminSettings: {
        enabledLanguages: []
      },
      settings: {
        active: false,
        defaultLanguage: '',
        reportsEndpoint: ''
      }
    }
)

const languageCodes = ref<Record<string, string>>({})

const fetchLanguageCodes = async () => {
  for (const langId of tenant.value.adminSettings.enabledLanguages) {
    const res = await axiosInstance.get(`/translations/${langId}`)
    languageCodes.value[langId] = res.data.languageCode
  }
}

const getLanguageIdentifier = (langId: string) => {
  const lang = allTranslations.value.find((lang) => lang._id === langId)
  return lang ? lang.languageIdentifier : ''
}

watch(() => tenant.value.adminSettings.enabledLanguages, fetchLanguageCodes, { immediate: true })

const saveSettings = async () => {
  try {
    const settingsCopy = {
      ...tenant.value.settings,
      faqFallbackLanguage:
        tenant.value.settings.faqFallbackLanguage === 'none'
          ? null
          : tenant.value.settings.faqFallbackLanguage
    }

    const res = await axiosInstance.put(`/config/updateTenantSettings/${tenant.value.id}`, {
      settings: settingsCopy
    })

    toast.add({ severity: 'success', summary: 'Success', detail: 'Settings saved successfully' })
  } catch (error) {
    toast.add({ severity: 'error', summary: 'Error', detail: 'Failed to save settings' })
  }
}

const toggleTranslationStatus = async (translationId: any) => {
  const translation = customTranslations.value.find((t) => t._id === translationId)
  const status = translation?.active

  if (translation.status && translation?.progress?.missingRequiredFields > 0) {
    toast.add({
      severity: 'warn',
      summary: 'Missing required fields',
      detail: 'This translation is missing required fields and cannot be activated.'
    })
    return
  }

  try {
    const res = await axiosInstance.put(`/translations/toggleTranslationStatus/${translationId}`)
    toast.add({
      severity: 'success',
      summary: 'Success',
      detail: res.data.message
    })
  } catch (error) {
    if (translation) translation.active = !status
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: error.response?.data?.message || error.message || 'Unexpected error'
    })
  }
}

const checkPermission = (state: boolean, progress: any) => {
  if (!state) {
    return progress?.missingRequiredFields > 0
  }
}

onMounted(async () => {
  gF.startLoading()
  try {
    //await getTenantDetails();
    tenantDetails.value = sessionStore.tenant || {}

    await getAllTranslations()
    await fetchLanguageCodes()
    await getCustomTranslations()
  } catch (error) {
    console.error('Error fetching tenant details:', error)
  } finally {
    gF.endLoading()
  }
})
</script>

<template>
  <div>
    <SettingsPageTemplate :saveDisabled="isSaving" @save="saveSettings()">
      <template #settings-content>
        <div class="p-3">

          <div class="row align-items-center mb-3">
            <label for="defaultLanguage" class="col-sm-2 col-form-label">Status</label>
            <div class="col-sm-9">
              <ToggleButton
                v-if="tenantDetails.settings"
                id="isActiveToggle"
                class="form-control"
                v-model="tenantDetails.settings.active"
                onLabel="Your AIRiN website is active"
                offLabel="Your AIRiN website is not active"
              />
            </div>
          </div>
          <div class="row align-items-center mb-3">
            <label for="defaultLanguage" class="col-sm-2 col-form-label">Redirect Page</label>
            <div class="col-sm-9">
              <InputText
                v-if="tenantDetails.settings"
                :disabled="tenantDetails.settings.active"
                id="redirectPage"
                class="form-control"
                v-model="tenantDetails.settings.redirectPage"
              />
              <Message size="small" class="form-text" severity="secondary" variant="simple">
                If your AIRiN website is deactivated, the user will be redirected to this website.
              </Message>
            </div>
          </div>
          <div class="row align-items-center mb-3">
            <label for="defaultLanguage" class="col-sm-2 col-form-label">Default Language</label>
            <div class="col-sm-9">
              <Select
                v-if="tenantDetails.settings"
                id="defaultLanguage"
                v-model="tenantDetails.settings.defaultLanguage"
                :options="fallbackLanguageOptions"
                class="w-100"
              >
                <template #option="slotProps">
                  <div class="flex items-center">
                    {{ getLanguageIdentifier(slotProps.option) }}
                  </div>
                </template>
                <template #value="slotProps">
                  <div class="flex items-center">
                    {{ getLanguageIdentifier(slotProps.value) }}
                  </div>
                </template>
              </Select>
              <Message size="small" class="form-text" severity="secondary" variant="simple">
                Initial Language Setting.
              </Message>
            </div>
          </div>
          <div class="row align-items-center mb-3">
            <label for="defaultLanguage" class="col-sm-2 col-form-label"
              >Default Language FAQ</label
            >
            <div class="col-sm-9">
              <Select
                v-if="tenantDetails.settings"
                id="defaultLanguage"
                v-model="faqFallbackBinding"
                :options="fallbackLanguageOptions"
                class="w-100"
              >
                <template #option="slotProps">
                  <div class="flex items-center">
                    {{
                      slotProps.option === 'none'
                        ? '– None –'
                        : getLanguageIdentifier(slotProps.option)
                    }}
                  </div>
                </template>
                <template #value="slotProps">
                  <div class="flex items-center">
                    {{
                      slotProps.value === 'none'
                        ? '– None –'
                        : getLanguageIdentifier(slotProps.value)
                    }}
                  </div>
                </template>
              </Select>
              <Message size="small" class="form-text" severity="secondary" variant="simple">
                If none is selected, the Default Language will be used for the FAQ.
              </Message>
            </div>
          </div>
        </div>
      </template>
    </SettingsPageTemplate>
  </div>
</template>
<style scoped>
.progressCard {
  margin-bottom: 1rem;
  border: 1px solid var(--bs-border-color-translucent);
  box-shadow: none;
  border-radius: var(--bs-border-radius);
}
</style>
